import type { FetchTicketResponse } from "src/api/models";
import type { Column } from "src/types/trello-board-type";
import type { MenuItem } from "./types";
import { createAssigneeMenuItem } from "./items/assignee";
import { createPriorityMenuItem } from "./items/priority";
import { createDatePickerMenuItem } from "./items/date-picker";
import { createArticleMenuItem } from "./items/article";
import { createCutMenuItem, createPasteMenuItem } from "./items/cut-paste";
import { createQuickViewMenuItem } from "./items/quick-view";
import { createCopyContactMenuItem } from "./items/copy-contact";
import { useTicketService } from "src/services/ticket-service";
import { format } from 'date-fns';
import { toLocalDateString } from "src/utils/date-utc";

interface MenuUIHandlers {
  selectAssigneeRef: any;
  showAddArticleDialog: () => void;
  showDatePicker: (event: MouseEvent) => void;
  showQuickView: () => void;
  showCreateTicket?: (date: string) => void;
  onCopyPhoneSuccess?: () => void;
}

export function createTrelloContextMenu(
  ticket: FetchTicketResponse | null,
  column: Column<FetchTicketResponse> | null,
  event: MouseEvent,
  refs: MenuUIHandlers,
  ticketService: ReturnType<typeof useTicketService>,
  emits: {
    onUpdateTicket: (ticket: FetchTicketResponse) => void;
    onUpdateTicketPlannedFor: (ticket: FetchTicketResponse) => void;
    onCut: (ticket: FetchTicketResponse) => void;
    onPaste: (ticket: FetchTicketResponse, column: Column<FetchTicketResponse>) => void;
  },
  cutTicket: FetchTicketResponse | null,
  showError: (options?: { message?: string }) => void
) {
  const items: MenuItem[] = [];

  if (column && column.date) {
    items.push({
      label: `Create ticket for ${format(column.date, 'd MMM')}`,
      icon: 'pi pi-plus',
      command: () => {
        refs.showCreateTicket?.(toLocalDateString(column.date));
      }
    });
  }

  if (ticket) {
    items.push(
      createAssigneeMenuItem(ticket, refs.selectAssigneeRef, {
        updateTicketAssignee: ticketService.updateTicketAssignee,
        onUpdateTicket: emits.onUpdateTicket
      }),
      createPriorityMenuItem(ticket, {
        updateTicketPriority: ticketService.updateTicketPriority,
        onUpdateTicket: emits.onUpdateTicket
      }),
      createDatePickerMenuItem(ticket, event, {
        showDatePicker: refs.showDatePicker
      }),
      createArticleMenuItem(ticket, refs.showAddArticleDialog),
      createCopyContactMenuItem(ticket, {
        onCopySuccess: refs.onCopyPhoneSuccess ?? (() => {}),
      }),
      createQuickViewMenuItem(ticket, { showQuickView: refs.showQuickView }),
      createCutMenuItem(ticket, cutTicket, {
        onCut: emits.onCut
      })
    );
  }

  if (column) {
    items.push(
      createPasteMenuItem(column, cutTicket, {
        updateTicketPlannedDate: ticketService.updateTicketPlannedDate,
        onPaste: emits.onPaste,
        showError,
      })
    );
  }

  return items;
}
